import React, { useMemo, useState } from 'react';
import type { TalentsState } from '../../hooks/useTalents';
import { TomeButton, TomeChapter, TomeFolio, TomeTabs } from '../../primitives/tome';
import { formatCallingName } from '../../theme';
import { TalentEntry } from './TalentEntry';
import { talentState, talentTiers } from './talentPresentation';
import type { TalentRequestSnapshot } from './TalentLoanStore';
import './TalentsTome.css';

export interface TalentsPageProps {
  talents: TalentsState;
  pending: TalentRequestSnapshot['pending'];
  feedback: TalentRequestSnapshot['feedback'];
  onSpend: (treeName: string, nodeId: string) => void;
  onRespec: () => void;
}

const treeLabel = (name: string) => name.replace(/[_-]/g, ' ').replace(/\b[a-z]/g, char => char.toUpperCase());

/**
 * The Talents page on the Tome: a folio with the calling and its points, the tree's talents in
 * chapters by how far along the prerequisites they sit, then the reset offer. The TomeWindow frame
 * supplies the parchment and the zone's --tome-* tokens, and scrolls the page.
 */
export const TalentsPage: React.FC<TalentsPageProps> = ({ talents: state, pending, feedback, onSpend, onRespec }) => {
  const { skillPoints, className, talents, hasTree, talentRespecOffer, gold } = state;
  const treeNames = useMemo(() => Object.keys(talents), [talents]);
  const [activeTree, setActiveTree] = useState('');
  const currentTree = treeNames.includes(activeTree) ? activeTree : treeNames[0] ?? '';
  const nodes = currentTree ? talents[currentTree] ?? [] : [];
  const allNodes = useMemo(() => Object.fromEntries(Object.values(talents).flat().map(node => [node.id, node])), [talents]);
  const quote = talentRespecOffer?.quote;
  const validQuote = quote?.action === 'talent_respec' && Number.isSafeInteger(quote.cost) && quote.cost >= 0;
  const respecCost = quote?.cost ?? 0;
  const respecNeedsGold = validQuote && gold < respecCost;
  return <section className="talents-tome" aria-label="Talents">
    <TomeFolio className="talents-tome__folio" aria-label="Your calling" shapeSeed={className ?? 'talents'} data-testid="talents-header">
      <span className="talents-tome__kicker">Shape your calling</span>
      <div className="talents-tome__heading">
        <h3>{className ? `${formatCallingName(className)} Talents` : 'No character selected'}</h3>
        <strong data-testid="talents-skill-points" data-points={skillPoints > 0 ? 'to-spend' : 'none'}>Available Points: {skillPoints}</strong>
      </div>
    </TomeFolio>
    {treeNames.length > 1 && <TomeTabs tabs={treeNames.map(name => ({ id: name, label: treeLabel(name) }))} active={currentTree}
      onSelect={setActiveTree} label="Talent trees" idPrefix="talents" />}
    {hasTree && nodes.length > 0 ? <div className="talents-tome__tree" id={`talents-panel-${currentTree}`} role={treeNames.length > 1 ? 'tabpanel' : undefined}
      aria-labelledby={treeNames.length > 1 ? `talents-tab-${currentTree}` : undefined} data-testid="talents-grid">
      {talentTiers(nodes, allNodes).map((tier, index) => <TomeChapter key={index} title={`Tier ${index + 1}`} count={tier.length}>
        {tier.map(node => <TalentEntry key={node.id} node={node} state={talentState(node, allNodes, skillPoints)} busy={Boolean(pending)}
          pending={pending?.kind === 'spend' && pending.treeName === currentTree && pending.nodeId === node.id}
          prerequisiteNames={node.prerequisites.map(id => allNodes[id]?.name ?? 'a required talent')}
          onSpend={() => onSpend(currentTree, node.id)} />)}
      </TomeChapter>)}
    </div> : <p className="talents-tome__empty" data-testid="talents-empty">
      {className === null ? 'Choose a character to explore their talents.' : 'No talent tree is listed for this calling.'}
    </p>}
    {feedback && <p className="talents-tome__feedback" role={feedback.refused ? 'alert' : 'status'} data-refused={feedback.refused || undefined}>{feedback.message}</p>}
    <footer className="talents-tome__notice" data-testid="talents-authority-notice">
      <p>Your ranks and available points change only after your choices are saved.</p>
      {talentRespecOffer && <div className="talents-tome__respec">
        <span>{validQuote ? (respecCost === 0 ? 'Reset cost: Free' : `Reset cost: ${respecCost} Gold`) : 'Reset cost unavailable'}</span>
        <TomeButton tone="secondary" cut="clean" disabled={Boolean(pending) || !talentRespecOffer.available || !validQuote || respecNeedsGold}
          onClick={onRespec} data-testid="talent-respec">
          {pending?.kind === 'respec' ? 'Resetting…' : !validQuote ? 'Reset unavailable' : respecNeedsGold ? `Need ${respecCost} Gold` : `Respec Talents — ${respecCost === 0 ? 'Free' : `${respecCost} Gold`}`}
        </TomeButton>
      </div>}
    </footer>
  </section>;
};
